/* eslint-disable react-hooks/exhaustive-deps */
import ExcelExportButton from 'components/ExcelExportButton/ExcelExportButton';
import {Grid, TableFooter, TablePagination, TableRow} from '@material-ui/core';
import {CustomButton} from 'commons/Button/CustomButton';
import {localize} from 'commons/LocalizedText/LocalizedText';
import {makeStyles} from '@material-ui/core/styles';
import React, {useEffect, useRef, useState} from 'react';
import {UserService} from 'services/UserService';
import {DialogType} from 'commons/Dialog/CustomDialog';
import {useDialog} from 'hook/DialogProvider';
import {
  firstnameRow,
  lastnameRow,
  mailOrLoginRow,
  profileRow,
  programRow,
  statusRow
} from 'components/LaboAdmin/LaboUserListRows';
import {convertJsonToXlsx} from 'utils/excelConverter';
import {useHistory, useParams} from 'react-router-dom';
import {CustomTableWithExternalPagination} from 'commons/Table/CustomTableWithExternalPagination';
import useQueryState from 'hook/UseQueryState';
import {ParamTypes} from 'utils/constants/param.type';
import {INITIAL_PAGE} from 'commons/Table/CustomTable';
import {GENERATE_ROUTE, ROUTES} from 'utils/constants/routes';
import {LaboUserSearch} from "commons/Filter/SearchFields/LaboUserSearch";

const useStyles = makeStyles((theme) => ({
  inputFields: {
    maxWidth: 600,
    width: '-webkit-fill-available',
    marginLeft: '20px'
  },
  addButton: {
    margin: '10px'
  },
  tableFooter: {
    marginTop: '20px'
  }
}));

const headers = [
  mailOrLoginRow,
  lastnameRow,
  firstnameRow,
  statusRow,
  profileRow,
  programRow
];

export const LaboUserList = () => {
  const classes = useStyles();
  const showDialog = useDialog();
  const history = useHistory();
  const { laboratory: laboratoryId } = useParams();
  const previousController = useRef();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [page, setPage] = useQueryState('page', INITIAL_PAGE, ParamTypes.NUMBER);
  const [rowsPerPage, setRowsPerPage] = useQueryState('rowsPerPage', 10, ParamTypes.NUMBER);
  const [contactGridFilterView, setContactGridFilterView] = useState({});

  const updateFilter = (filterData) => {
    setContactGridFilterView(filterData, ...laboratoryId);
  }

  useEffect(() => {
    if(Object.keys(contactGridFilterView).length !== 0) {
      setRows([]);
      updateData();
    }
  }, [contactGridFilterView]);

  const updateData = () => {
    if (laboratoryId == null) {
      return;
    }

    setIsLoading(true);
    if (previousController.current) {
      previousController.current.abort();
    }
    const controller = new AbortController();
    const { signal } = controller;
    previousController.current = controller;

    UserService.searchUsersWithFilters({...contactGridFilterView, laboratoryId}, signal)
      .then((response) => {
        if (response) {
          setRows([...response.users]);
          setTotal(response.count);
        }
        setIsLoading(false);
      })
      .catch((errorMessage) => {
        if (errorMessage !== 'AbortError') {
          showDialog({
            variant: DialogType.ERROR,
            title: 'error',
            description: errorMessage
          });
        }
      });
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const excelExport = () => {
    setIsLoadingExport(true);
    if (laboratoryId == null) {
      return;
    }
    UserService.searchUsersWithFilters({laboratoryId})
      .then((response) => {
        if (response) {
          if (response === '') {
            throw new Error("Missing data")
          }
          const date = new Date();
          const time = date.getTime();
          convertJsonToXlsx(response.users, `${localize('laboUsersExport')}${date.toLocaleDateString()}-${time}`);
        }
        setIsLoadingExport(false);
      })
      .catch((errorMessage) => {
        if (errorMessage !== 'AbortError') {
          showDialog({
            variant: DialogType.ERROR,
            title: 'error',
            description: errorMessage
          });
        }
        setIsLoadingExport(false);
      });
  }

  const openCreateUser = () => {
    const editUserLink = GENERATE_ROUTE(
      ROUTES.LABO_ADMIN.USER_CREATE,
      {laboratory: laboratoryId}
    );
    history.push(editUserLink);
  }

  const openEditUser = (row) => {
    const editUserLink = GENERATE_ROUTE(
      ROUTES.LABO_ADMIN.USER_EDIT,
      { laboratory: laboratoryId, personContactDetailsId: row.personContactDetailsRecid }
    );
    history.push(editUserLink);
  }

  return <div className={classes.marginTop}>
    <Grid
      alignItems="flex-start"
      container
      direction="column"
      justifyContent="space-evenly"
      spacing={15}
      wrap='nowrap'
    >
      <Grid
        item>
        <LaboUserSearch
          updateFilter={updateFilter}
          withCompany={false}
        />
      </Grid>
    </Grid>
    <Grid
      alignItems="center"
      container
      direction="row"
      justifyContent="space-evenly"
      spacing={10}
      wrap='nowrap'>
      <Grid item xs />
      <Grid
        item
        justifyContent="flex-start"
      >
        <CustomButton
          className={classes.addButton}
          onClick={() => openCreateUser()}
        >
          {localize('addUserButton')}
        </CustomButton>
      </Grid>
    </Grid>

    <CustomTableWithExternalPagination
      headers={headers}
      isLoading={isLoading}
      onRowCLick={(row) => openEditUser(row)}
      page={page}
      rowKey="mailOrLoginRow"
      rows={rows}
      rowsPerPage={rowsPerPage}
      setPage={setPage}
      total={total}
    />
    <Grid alignItems='center' className={classes.tableFooter} container justifyContent='space-between'>
      <Grid item>
        <ExcelExportButton
          handleExport={() => excelExport()}
          isLoadingExport={isLoadingExport}>
        </ExcelExportButton>
      </Grid>
      <Grid item>
        <TableFooter>
          <TableRow>
            <TablePagination
              backIconButtonText={localize('previousPage')}
              className={classes.overflowHidden}
              count={total}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to === -1 ? count : to} ${localize('to')} ${count}`}
              labelRowsPerPage={localize('rowsPerPage')}
              nextIconButtonText={localize('nextPage')}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </TableRow>
        </TableFooter>
      </Grid>
    </Grid>
  </div>;
}